// Forces a logout when the current user's permissions change mid-session
// (an admin edits their role, or reassigns them to a different role, while
// they're already logged in elsewhere). There's no backend push channel
// (websocket/SSE) for this, so it's done by periodically re-fetching every
// permission group this session has already resolved and comparing against
// the cached value — on any mismatch, the session is cleared and the user is
// sent back to the login page, the same way an expired JWT is handled in
// axiosInstance.js.
//
// Started once per app load from PrivateRoute (the first authenticated route
// to mount) — startPermissionWatcher() is idempotent, so every PrivateRoute
// instance calling it is harmless.
import { toast } from 'react-toastify';
import API from '../helpers/axiosInstance';
import { getApiHeaders } from './DefaultFunction';
import { getAllCachedGroups, getCachedPermissions, clearPermissionCache } from './permissionCache';

const POLL_INTERVAL_MS = 60 * 1000; // 1 minute

let started = false;
let intervalId = null;
let checking = false;

// Order-independent fingerprint of a permission list, so re-fetching the same
// group twice with the same (possibly reordered) data never false-positives.
function fingerprint(list) {
    return (list || [])
        .map((p) => `${p.slug}:${p.permission ? 1 : 0}`)
        .sort((a, b) => a.localeCompare(b))
        .join('|');
}

async function checkForPermissionChanges() {
    if (checking) return; // a previous check is still in flight — skip this tick
    const token = localStorage.getItem('customer-portal');
    if (!token) return; // not logged in — nothing to watch

    const groups = getAllCachedGroups();
    if (groups.length === 0) return;

    checking = true;
    try {
        for (const group of groups) {
            const cached = getCachedPermissions(group);
            if (cached === undefined) continue;
            try {
                const url = `${process.env.REACT_APP_API_URL}/permission/feature/list?slug=${group}`;
                const response = await API.get(url, { headers: getApiHeaders() });
                const fresh = response.data.data || [];
                if (fingerprint(fresh) !== fingerprint(cached)) {
                    forceLogoutForPermissionChange();
                    return;
                }
            } catch (error) {
                // A 403 here (unlike elsewhere in the app) IS evidence of a
                // permission change — this exact group resolved fine a moment ago
                // and is now forbidden, meaning access to it was just revoked.
                // Any other failure (network blip, 5xx) is transient and skipped;
                // axiosInstance's own interceptor already handles a genuinely
                // expired/invalid session (401).
                if (error?.response?.status === 403) {
                    forceLogoutForPermissionChange();
                    return;
                }
            }
        }
    } finally {
        checking = false;
    }
}

function forceLogoutForPermissionChange() {
    stopPermissionWatcher();
    toast.error('Your access has changed. Please log in again.');
    localStorage.clear();
    clearPermissionCache();
    window.location.href = '/';
}

function onVisibilityChange() {
    if (document.visibilityState === 'visible') checkForPermissionChanges();
}

// Sync wrapper so the async checker isn't passed directly as an event listener
// (listeners expect a void return). Named so add/removeEventListener match.
function onFocus() {
    checkForPermissionChanges();
}

export function startPermissionWatcher() {
    if (started) return;
    started = true;
    intervalId = setInterval(checkForPermissionChanges, POLL_INTERVAL_MS);
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', onFocus);
}

export function stopPermissionWatcher() {
    if (intervalId) clearInterval(intervalId);
    intervalId = null;
    started = false;
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('focus', onFocus);
}
